import React, { useState } from "react";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Label,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Textarea,
  Tooltip,
  makeStyles,
} from "@fluentui/react-components";
import {
  TableCell,
  TableCellLayout,
  TableRow,
  TableRowData as RowStateBase,
} from "@fluentui/react-components/unstable";
import { MoreHorizontalRegular, WarningRegular } from "@fluentui/react-icons";
import { ListChildComponentProps } from "react-window";
import { AppConfirmdialog } from "../common";
import { IResourceAllocationSummaryRowApi } from "../../services/types/resourceAllocation";
import { formatResourceAllocationDuration } from "../../utils/resourceAllocationDuration";
import { getContractTypeLabel } from "../../utils/contractType";
import {
  formatRejectionDetailLine,
  resolveDecisionBadgePresentation,
  resolveRejectedStatusMeta,
  resolveResourceAllocationDecision,
} from "./resourceAllocationRowPresentation";
import styles from "./ExpectedHoursRowStyles";
import listStyles from "../layouts/EmplyeesListStyles";

const useRejectDialogStyles = makeStyles({
  dialogContainer: {
    maxWidth: "450px",
  },
});

const complianceIconSize = 20;

interface ReactWindowRenderFnProps extends ListChildComponentProps {
  data: TableRowData[];
  readOnlyMode: boolean;
  acceptSettlement?: (row: IResourceAllocationSummaryRowApi) => Promise<void>;
  rejectSettlement?: (row: IResourceAllocationSummaryRowApi, rejectionReason: string) => Promise<void>;
  onRowClick: (row: IResourceAllocationSummaryRowApi) => void;
}

interface TableRowData extends RowStateBase<IResourceAllocationSummaryRowApi> {}

function renderRejectionReasonCell(item: IResourceAllocationSummaryRowApi, readOnlyMode: boolean) {
  if (readOnlyMode) {
    if (!item.rejectionDetails?.length) {
      return "—";
    }

    if (item.rejectionDetails.length === 1) {
      const line = formatRejectionDetailLine(item.rejectionDetails[0]);
      return (
        <Tooltip relationship="description" content={line}>
          <span style={styles.rejectionReasonText}>{line}</span>
        </Tooltip>
      );
    }

    return (
      <Tooltip
        relationship="description"
        content={
          <div>
            {item.rejectionDetails.map((detail, index) => (
              <div key={`${detail.managerLastName}-${detail.projectName}-${index}`}>
                {formatRejectionDetailLine(detail)}
              </div>
            ))}
          </div>
        }
      >
        <span style={styles.rejectionReasonText}>
          {`${item.rejectionDetails.length} odrzucenia — najedź, aby zobaczyć szczegóły`}
        </span>
      </Tooltip>
    );
  }

  const reason = item.rejectionReason?.trim();
  if (!reason) {
    return "—";
  }

  return (
    <Tooltip relationship="description" content={reason}>
      <span style={styles.rejectionReasonText}>{reason}</span>
    </Tooltip>
  );
}

const ResourceAllocationSummaryRow: React.FC<ReactWindowRenderFnProps> = ({
  data,
  index,
  style,
  readOnlyMode,
  acceptSettlement,
  rejectSettlement,
  onRowClick,
}) => {
  const [openAcceptDialog, setOpenAcceptDialog] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectValidationError, setRejectValidationError] = useState("");

  const row = data[index];
  const item = row.item;
  const rejectDialogStyles = useRejectDialogStyles();
  const decision = resolveResourceAllocationDecision(
    item.settlementStatus,
    item.hasResourceAllocationOrders
  );
  const badgePresentation = resolveDecisionBadgePresentation(decision);
  const rejectedStatusMeta = resolveRejectedStatusMeta();
  const showActions = item.canAcceptSettlement || item.canRejectSettlement;

  const resetRejectDialogState = () => {
    setRejectionReason("");
    setRejectValidationError("");
  };

  const handleAccept = async () => {
    if (!acceptSettlement) return;
    setIsSubmitting(true);
    try {
      await acceptSettlement(item);
      setOpenAcceptDialog(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectSettlement) return;
    const trimmedReason = rejectionReason.trim();
    if (!trimmedReason) {
      setRejectValidationError("Powód odrzucenia jest wymagany.");
      return;
    }

    setRejectValidationError("");
    setIsSubmitting(true);
    try {
      await rejectSettlement(item, trimmedReason);
      setOpenRejectDialog(false);
      resetRejectDialogState();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <TableRow
        style={style}
        aria-rowindex={index + 2}
        onClick={() => onRowClick(item)}
        key={item.employeeId}
      >
        <TableCell>
          <TableCellLayout>{`${item.lastName} ${item.firstName}`}</TableCellLayout>
        </TableCell>
        <TableCell>
          <TableCellLayout>{getContractTypeLabel(item.contractType)}</TableCellLayout>
        </TableCell>
        <TableCell>
          <TableCellLayout>{formatResourceAllocationDuration(item.orderedMinutes)}</TableCellLayout>
        </TableCell>
        <TableCell>
          <TableCellLayout>
            {formatResourceAllocationDuration(item.loggedMinutes ?? 0)}
          </TableCellLayout>
        </TableCell>
        <TableCell>
          <TableCellLayout>
            {formatResourceAllocationDuration(item.acceptedYouTrackMinutes ?? 0)}
          </TableCellLayout>
        </TableCell>
        <TableCell style={styles.statusCell}>
          <TableCellLayout style={styles.statusCellLayout}>
            {badgePresentation ? (
              <>
                <Badge
                  shape="rounded"
                  size="medium"
                  appearance={badgePresentation.appearance}
                  color={badgePresentation.color}
                  style={styles.decisionBadge}
                >
                  {badgePresentation.label}
                </Badge>
                {!readOnlyMode && decision === "rejected" ? (
                  <Tooltip relationship="label" content={rejectedStatusMeta.label}>
                    <span
                      role="img"
                      aria-label={rejectedStatusMeta.label}
                      style={{
                        ...styles.complianceIconTrigger,
                        ...styles.complianceIconInconsistent,
                        marginLeft: 6,
                      }}
                    >
                      <WarningRegular aria-hidden fontSize={complianceIconSize} />
                    </span>
                  </Tooltip>
                ) : null}
              </>
            ) : (
              "—"
            )}
          </TableCellLayout>
        </TableCell>
        <TableCell style={styles.rejectionReasonCell}>
          <TableCellLayout>{renderRejectionReasonCell(item, readOnlyMode)}</TableCellLayout>
        </TableCell>
        {!readOnlyMode ? (
        <TableCell style={styles.actionsCell} onClick={(e) => e.stopPropagation()}>
          <TableCellLayout style={styles.actionsCellLayout}>
            {showActions && acceptSettlement && rejectSettlement ? (
              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  <Button
                    size="small"
                    appearance="outline"
                    icon={<MoreHorizontalRegular />}
                    disabled={isSubmitting}
                    aria-label="Akcje"
                  />
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    {item.canAcceptSettlement ? (
                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenAcceptDialog(true);
                        }}
                      >
                        Zaakceptuj
                      </MenuItem>
                    ) : null}
                    {item.canRejectSettlement ? (
                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          resetRejectDialogState();
                          setOpenRejectDialog(true);
                        }}
                      >
                        Odrzuć
                      </MenuItem>
                    ) : null}
                  </MenuList>
                </MenuPopover>
              </Menu>
            ) : null}
          </TableCellLayout>
        </TableCell>
        ) : null}
      </TableRow>

      <AppConfirmdialog
        title="Potwierdź akceptację"
        bodyTxt="Czy na pewno chcesz zaakceptować rozliczenie godzin pracy tego pracownika?"
        setOpen={setOpenAcceptDialog}
        open={openAcceptDialog}
        onAccept={handleAccept}
        isLoading={isSubmitting}
      />

      <Dialog
        open={openRejectDialog}
        onOpenChange={(_, data) => {
          if (!data.open) {
            setOpenRejectDialog(false);
            resetRejectDialogState();
          }
        }}
      >
        <DialogSurface className={rejectDialogStyles.dialogContainer}>
          <DialogBody>
            <DialogTitle>Odrzuć rozliczenie</DialogTitle>
            <DialogContent>
              <div style={listStyles.dialogFormField}>
                <Label required>Powód odrzucenia</Label>
                <Textarea
                  style={{ width: "100%" }}
                  placeholder="Powód odrzucenia"
                  value={rejectionReason}
                  onChange={(_, data) => setRejectionReason(data.value)}
                  resize="vertical"
                />
              </div>
              {rejectValidationError ? (
                <span style={{ color: "red" }}>{rejectValidationError}</span>
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setOpenRejectDialog(false)}>
                Anuluj
              </Button>
              <Button appearance="primary" onClick={handleReject} disabled={isSubmitting}>
                Odrzuć
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};

export default ResourceAllocationSummaryRow;
